import React, { useState, useEffect, useMemo, useCallback } from 'react';
import RosteringPlanDialog from '../dialogs/RosteringPlanDialog';
import RosterFlightDutyChip from './RosterFlightDutyChip';
import CrewClearanceChips from './CrewClearanceChips';
import { isCrewRosterHighlightCleared } from '../../../../utils/crewClearance';
import {
  Paper,
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  CircularProgress,
  Dialog,
  AppBar,
  Toolbar
} from '@mui/material';
import { Add, ChevronLeft, ChevronRight, Today, Delete, Fullscreen, FullscreenExit, Close } from '@mui/icons-material';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import axios from 'axios';
import { useToast } from '../../../../contexts/ToastContext';

dayjs.extend(utc);
dayjs.extend(timezone);

const API_URL = process.env.REACT_APP_API_URL || '/api';

/** Plan work/off rows created from Rostering → Create duty roster */
function isPlanningEntry(entry) {
  if (entry?.status === 'CANCELLED') return false;
  return entry?.dutyType === 'SCHEDULED_DUTY_OFF' || entry?.rosterPlanOnly === true;
}

/**
 * Rostering grid: plan rows + Ops-assigned flights (soft-synced linked FLIGHT)
 * so allocated pilots show on the day with aircraft registration.
 */
function isRosteringVisibleEntry(entry) {
  if (entry?.status === 'CANCELLED') return false;
  if (isPlanningEntry(entry)) return true;
  if (entry?.dutyType !== 'FLIGHT') return false;
  return entry?.linkedToFlight === true || Boolean(entry?.sectorId);
}

/** UTC ms for start/end of a calendar date in IANA tz */
function dayBoundsInTz(dateStr, tz) {
  const start = dayjs.tz(`${dateStr}T00:00:00`, tz).valueOf();
  const end = dayjs.tz(`${dateStr}T23:59:59.999`, tz).valueOf();
  return { start, end };
}

function entryIntersectsDisplayDay(entry, dateStr, tz) {
  const { start: dayStart, end: dayEnd } = dayBoundsInTz(dateStr, tz);
  const ds = new Date(entry.dutyStartTime).getTime();
  const de = new Date(entry.dutyEndTime).getTime();
  return ds <= dayEnd && de >= dayStart;
}

const RosteringPlanPanel = ({
  crewMembers,
  displayTimezone = 'Asia/Kolkata',
  filters,
  defaultPlannedOffDays,
  onViewDuty,
  onRefreshParent,
  canDeletePlanning = false
}) => {
  const { showToast } = useToast();
  const tz = displayTimezone || 'UTC';
  /** Drives prev/next: week | month | days45; null when range comes from parent filters */
  const [rangePreset, setRangePreset] = useState('week');
  const [rangeStart, setRangeStart] = useState(() => dayjs().startOf('week'));
  const [rangeEnd, setRangeEnd] = useState(() => dayjs().endOf('week'));
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [fullScreenOpen, setFullScreenOpen] = useState(false);

  useEffect(() => {
    if (filters?.startDate && filters?.endDate) {
      setRangeStart(dayjs(filters.startDate));
      setRangeEnd(dayjs(filters.endDate));
      setRangePreset(null);
    }
  }, [filters?.startDate, filters?.endDate]);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        startDate: rangeStart.format('YYYY-MM-DD'),
        endDate: rangeEnd.format('YYYY-MM-DD'),
        overlapDates: 'true'
      });
      if (filters?.crewId) params.append('crewId', filters.crewId);
      const res = await axios.get(`${API_URL}/ops/crew-duty-roster?${params}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const list = (res.data || []).filter(isRosteringVisibleEntry);
      setEntries(list);
    } catch (e) {
      console.error(e);
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [rangeStart, rangeEnd, filters?.crewId]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const dates = useMemo(() => {
    const out = [];
    let d = rangeStart;
    while (d.isBefore(rangeEnd) || d.isSame(rangeEnd, 'day')) {
      out.push(d);
      d = d.add(1, 'day');
    }
    return out;
  }, [rangeStart, rangeEnd]);

  const crewIdsWithPlanning = useMemo(() => {
    const ids = new Set();
    (entries || []).forEach((e) => {
      const id = e.crewId?._id || e.crewId;
      if (id != null) ids.add(String(id));
    });
    return ids;
  }, [entries]);

  /** Pilots with a plan block and/or an Ops-assigned flight in this date range */
  const crewRows = useMemo(() => {
    const hasRoster = (c) => crewIdsWithPlanning.has(String(c._id));
    const baseOk = (c) => {
      if (!filters?.baseCity) return true;
      return String(c?.workLocation?.city || '').trim().toLowerCase()
        === String(filters.baseCity).trim().toLowerCase();
    };
    if (filters?.crewId) {
      const one = crewMembers.find((c) => String(c._id) === String(filters.crewId));
      if (!one || !hasRoster(one) || !baseOk(one)) return [];
      return [one];
    }
    return crewMembers
      .filter((c) => hasRoster(c) && baseOk(c))
      .sort((a, b) =>
        `${a.firstName || ''} ${a.lastName || ''}`.localeCompare(`${b.firstName || ''} ${b.lastName || ''}`)
      );
  }, [crewMembers, filters?.crewId, filters?.baseCity, crewIdsWithPlanning]);

  const cellsFor = useCallback(
    (crewId, dateStr) => {
      const hits = entries.filter(
        (e) =>
          String(e.crewId?._id || e.crewId) === String(crewId) && entryIntersectsDisplayDay(e, dateStr, tz)
      );
      return hits;
    },
    [entries, tz]
  );

  const openDuty = (entry) => {
    const id = entry?._id;
    if (id && onViewDuty) onViewDuty(id);
  };

  const cancelPlanningSegment = async (e, entry) => {
    e.stopPropagation();
    if (!entry?._id) return;
    if (
      !window.confirm(
        'Cancel this roster segment? It will be marked cancelled (work and off blocks are separate rows — cancel each you need removed).'
      )
    ) {
      return;
    }
    try {
      await axios.delete(`${API_URL}/ops/crew-duty-roster/${entry._id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      showToast('Roster segment cancelled', 'success');
      await fetchEntries();
      if (onRefreshParent) onRefreshParent();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to cancel roster segment', 'error');
    }
  };

  const shiftWeek = (dir) => {
    const delta = dir * 7;
    setRangeStart((s) => s.add(delta, 'day').startOf('week'));
    setRangeEnd((e) => e.add(delta, 'day').endOf('week'));
  };

  const shiftMonth = (dir) => {
    setRangeStart((s) => {
      const nextStart = s.add(dir, 'month').startOf('month');
      setRangeEnd(nextStart.endOf('month'));
      return nextStart;
    });
  };

  /** 45 inclusive days starting at range start */
  const shift45DayWindow = (dir) => {
    setRangeStart((s) => {
      const nextStart = s.add(dir * 45, 'day').startOf('day');
      setRangeEnd(nextStart.add(44, 'day').endOf('day'));
      return nextStart;
    });
  };

  const shiftPeriod = (dir) => {
    if (rangePreset === 'month') shiftMonth(dir);
    else if (rangePreset === 'days45') shift45DayWindow(dir);
    else shiftWeek(dir);
  };

  const goThisWeek = () => {
    const t = dayjs();
    setRangePreset('week');
    setRangeStart(t.startOf('week'));
    setRangeEnd(t.endOf('week'));
  };

  const goThisMonth = () => {
    const t = dayjs();
    setRangePreset('month');
    setRangeStart(t.startOf('month'));
    setRangeEnd(t.endOf('month'));
  };

  const goNext45Days = () => {
    const t = dayjs();
    setRangePreset('days45');
    setRangeStart(t.startOf('day'));
    setRangeEnd(t.add(44, 'day').endOf('day'));
  };

  const rangeToolbar = (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
      <Button variant="contained" startIcon={<Add />} onClick={() => setCreateOpen(true)}>
        Create duty roster
      </Button>
      <IconButton size="small" onClick={() => shiftPeriod(-1)} aria-label="Previous range">
        <ChevronLeft />
      </IconButton>
      <IconButton size="small" onClick={() => shiftPeriod(1)} aria-label="Next range">
        <ChevronRight />
      </IconButton>
      <Button
        size="small"
        variant={rangePreset === 'week' ? 'contained' : 'outlined'}
        startIcon={<Today />}
        onClick={goThisWeek}
      >
        This week
      </Button>
      <Button
        size="small"
        variant={rangePreset === 'month' ? 'contained' : 'outlined'}
        onClick={goThisMonth}
      >
        This month
      </Button>
      <Button
        size="small"
        variant={rangePreset === 'days45' ? 'contained' : 'outlined'}
        onClick={goNext45Days}
      >
        Next 45 days
      </Button>
      <Tooltip title={fullScreenOpen ? 'Exit full screen' : 'Open full screen'}>
        <Button
          size="small"
          variant="outlined"
          startIcon={fullScreenOpen ? <FullscreenExit /> : <Fullscreen />}
          onClick={() => setFullScreenOpen((v) => !v)}
          aria-label={fullScreenOpen ? 'Exit full screen' : 'Open full screen'}
        >
          {fullScreenOpen ? 'Exit full screen' : 'Full screen'}
        </Button>
      </Tooltip>
    </Stack>
  );

  const legendRow = (
    <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
      <Typography variant="caption" color="text.secondary">
        Flight (VIP / IR / HILL / CP / TRI / TRE / CHPL / ATPL / Dual Type — gold / otherwise — light blue); city code color = location:
      </Typography>
      <RosterFlightDutyChip
        entry={{ dutyType: 'FLIGHT', dutyWorkLocationCity: 'Bengaluru', dutyWorkLocationCode: '' }}
        highlightCleared
      />
      <RosterFlightDutyChip
        entry={{ dutyType: 'FLIGHT', dutyWorkLocationCity: 'Bengaluru', dutyWorkLocationCode: '' }}
        highlightCleared={false}
      />
      <Chip size="small" label="Scheduled off" sx={{ bgcolor: '#f3e5f5', border: '1px solid #9c27b0' }} />
      <Chip size="small" label="Unavailable" variant="outlined" sx={{ color: 'text.secondary' }} />
      <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
        Cell borders: blue = assigned flight · green = plan · purple = off
      </Typography>
    </Stack>
  );

  const renderGrid = (maxHeight) => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={32} />
        </Box>
      );
    }
    return (
      <TableContainer
        sx={{
          maxHeight,
          flex: maxHeight === 'none' || maxHeight === '100%' ? 1 : undefined,
          height: maxHeight === '100%' ? '100%' : undefined,
          border: '2px solid',
          borderColor: 'primary.main',
          borderRadius: 1,
          '& .MuiTableCell-root': {
            border: '1px solid',
            borderColor: 'divider'
          },
          '& .MuiTableHead .MuiTableCell-root': {
            borderBottom: '2px solid',
            borderBottomColor: 'primary.main',
            bgcolor: 'grey.50'
          }
        }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  minWidth: 200,
                  position: 'sticky',
                  left: 0,
                  zIndex: 3,
                  bgcolor: 'grey.50',
                  borderRight: '2px solid',
                  borderRightColor: 'primary.main'
                }}
              >
                Pilot
              </TableCell>
              {dates.map((d) => (
                <TableCell key={d.format('YYYY-MM-DD')} align="center" sx={{ minWidth: 120 }}>
                  <Typography variant="caption" display="block">
                    {d.format('ddd')}
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {d.format('DD MMM')}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {crewRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={Math.max(1, dates.length + 1)} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No roster plans or assigned flights in this date range. Use <strong>Create duty roster</strong> for a
                  plan, or assign a pilot on a sector in Ops — they appear here with aircraft registration.
                </TableCell>
              </TableRow>
            ) : (
              crewRows.map((crew) => (
                <TableRow key={crew._id} hover>
                  <TableCell
                    sx={{
                      position: 'sticky',
                      left: 0,
                      zIndex: 2,
                      bgcolor: 'background.paper',
                      fontWeight: 500,
                      borderRight: '2px solid',
                      borderRightColor: 'primary.main'
                    }}
                  >
                    <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                      <span>
                        {crew.firstName} {crew.lastName}
                      </span>
                      <CrewClearanceChips crew={crew} />
                    </Stack>
                  </TableCell>
                  {dates.map((d) => {
                    const dateStr = d.format('YYYY-MM-DD');
                    const hits = cellsFor(crew._id, dateStr);
                    const unavailable = hits.length === 0;
                    const hasLinkedFlight = hits.some(
                      (e) => e.dutyType === 'FLIGHT' && (e.linkedToFlight === true || e.sectorId) && !e.rosterPlanOnly
                    );
                    const hasPlanFlight = hits.some((e) => e.rosterPlanOnly === true);
                    const hasOff = hits.some((e) => e.dutyType === 'SCHEDULED_DUTY_OFF');
                    const filledBorderColor = hasLinkedFlight
                      ? '#1565c0'
                      : hasPlanFlight
                        ? '#2e7d32'
                        : hasOff
                          ? '#7b1fa2'
                          : 'divider';
                    return (
                      <TableCell
                        key={dateStr}
                        align="center"
                        sx={{
                          p: 0.5,
                          verticalAlign: 'middle',
                          bgcolor: unavailable ? 'transparent' : 'rgba(25, 118, 210, 0.04)'
                        }}
                      >
                        {unavailable ? (
                          <Box
                            sx={{
                              minHeight: 48,
                              borderRadius: 1,
                              bgcolor: 'action.hover',
                              border: '1.5px dashed',
                              borderColor: 'grey.400'
                            }}
                          />
                        ) : (
                          <Tooltip title="Click chip to open; trash cancels this plan segment">
                            <Stack
                              spacing={0.5}
                              alignItems="center"
                              sx={{
                                width: '100%',
                                minHeight: 48,
                                py: 0.5,
                                px: 0.25,
                                borderRadius: 1,
                                border: '2px solid',
                                borderColor: filledBorderColor,
                                boxShadow: `inset 0 0 0 1px ${filledBorderColor}22`,
                                bgcolor: 'background.paper'
                              }}
                            >
                              {hits.map((e) => (
                                <Stack
                                  key={e._id}
                                  direction="row"
                                  spacing={0.25}
                                  alignItems="center"
                                  justifyContent="center"
                                  sx={{ width: '100%', maxWidth: 200 }}
                                >
                                  <Box
                                    onClick={() => openDuty(e)}
                                    sx={{ cursor: 'pointer', display: 'flex', justifyContent: 'center', flex: 1, minWidth: 0 }}
                                  >
                                    <RosterFlightDutyChip
                                      entry={e}
                                      highlightCleared={isCrewRosterHighlightCleared(crew)}
                                    />
                                  </Box>
                                  {canDeletePlanning && isPlanningEntry(e) && e.status !== 'CANCELLED' && (
                                    <Tooltip title="Cancel this plan segment">
                                      <IconButton
                                        size="small"
                                        aria-label="Cancel roster segment"
                                        onClick={(ev) => cancelPlanningSegment(ev, e)}
                                        sx={{ p: 0.25, flexShrink: 0 }}
                                      >
                                        <Delete fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </Stack>
                              ))}
                            </Stack>
                          </Tooltip>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={2} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h6">Rostering</Typography>
          <Typography variant="body2" color="text.secondary">
            Pilots with a roster plan or an Ops-assigned flight in this range are listed. Assigned flights show aircraft
            registration. Empty cells are unavailable (visual only). Times use {tz}.
            {canDeletePlanning && (
              <>
                {' '}
                Use the trash icon on a <strong>plan</strong> cell to cancel that segment (linked flights are edited from Ops / Table View).
              </>
            )}
          </Typography>
        </Box>
        {rangeToolbar}
      </Stack>

      <Stack spacing={1} sx={{ mb: 2 }}>
        {legendRow}
      </Stack>

      {renderGrid(560)}

      <Dialog
        fullScreen
        open={fullScreenOpen}
        onClose={() => setFullScreenOpen(false)}
        aria-labelledby="rostering-fullscreen-title"
      >
        <AppBar sx={{ position: 'relative' }} color="default" elevation={1}>
          <Toolbar sx={{ gap: 1, flexWrap: 'wrap', py: 1 }}>
            <Typography id="rostering-fullscreen-title" variant="h6" sx={{ flexGrow: 1, minWidth: 120 }}>
              Rostering
            </Typography>
            {rangeToolbar}
            <Tooltip title="Close full screen (Esc)">
              <IconButton edge="end" color="inherit" onClick={() => setFullScreenOpen(false)} aria-label="Close full screen">
                <Close />
              </IconButton>
            </Tooltip>
          </Toolbar>
        </AppBar>
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)', gap: 1.5, overflow: 'hidden' }}>
          {legendRow}
          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            {renderGrid('100%')}
          </Box>
        </Box>
      </Dialog>

      <RosteringPlanDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        crewMembers={crewMembers}
        displayTimezone={displayTimezone}
        defaultPlannedOffDays={defaultPlannedOffDays}
        onCreated={() => {
          fetchEntries();
          if (onRefreshParent) onRefreshParent();
        }}
      />
    </Paper>
  );
};

export default RosteringPlanPanel;
